import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type TrackingScriptType = 'gtm' | 'ga4' | 'meta' | 'tiktok' | 'linkedin' | 'custom';
export type TrackingScriptPosition = 'head' | 'body_start' | 'body_end';

export interface TrackingScript {
  id: string;
  name: string;
  type: TrackingScriptType;
  script_id: string | null;
  is_active: boolean;
  position: TrackingScriptPosition;
  custom_code: string | null;
  priority: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface TrackingScriptInput {
  name: string;
  type: TrackingScriptType;
  script_id?: string | null;
  is_active?: boolean;
  position?: TrackingScriptPosition;
  custom_code?: string | null;
  priority?: number;
}

export function useTrackingScripts() {
  const [scripts, setScripts] = useState<TrackingScript[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tracking_scripts')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      setScripts((data as TrackingScript[]) || []);
    } catch (error: any) {
      console.error('Error fetching tracking scripts:', error);
      toast({
        title: 'Erro ao carregar scripts',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const addScript = async (input: TrackingScriptInput): Promise<boolean> => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('tracking_scripts')
        .insert({
          name: input.name,
          type: input.type,
          script_id: input.script_id || null,
          is_active: input.is_active ?? true,
          position: input.position || 'head',
          custom_code: input.custom_code || null,
          priority: input.priority || 0,
          created_by: userData.user.id,
        });

      if (error) throw error;

      toast({
        title: 'Script adicionado',
        description: `${input.name} foi adicionado com sucesso.`,
      });
      
      await fetchScripts();
      return true;
    } catch (error: any) {
      console.error('Error adding tracking script:', error);
      toast({
        title: 'Erro ao adicionar script',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const updateScript = async (id: string, updates: Partial<TrackingScriptInput>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('tracking_scripts')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Script atualizado',
        description: 'As alterações foram salvas com sucesso.',
      });
      
      await fetchScripts();
      return true;
    } catch (error: any) {
      console.error('Error updating tracking script:', error);
      toast({
        title: 'Erro ao atualizar script',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteScript = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('tracking_scripts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Script removido',
        description: 'O script foi removido com sucesso.',
      });
      
      await fetchScripts();
      return true;
    } catch (error: any) {
      console.error('Error deleting tracking script:', error);
      toast({
        title: 'Erro ao remover script',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  const toggleActive = async (id: string, isActive: boolean): Promise<boolean> => {
    return updateScript(id, { is_active: isActive });
  };

  return {
    scripts,
    loading,
    fetchScripts,
    addScript,
    updateScript,
    deleteScript,
    toggleActive,
  };
}

// Hook separado para carregar apenas scripts ativos (usado pelo loader)
export function useActiveTrackingScripts() {
  const [scripts, setScripts] = useState<TrackingScript[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveScripts = async () => {
      try {
        const { data, error } = await supabase
          .from('tracking_scripts')
          .select('*')
          .eq('is_active', true)
          .order('priority', { ascending: true });

        if (error) throw error;
        setScripts((data as TrackingScript[]) || []);
      } catch (error) {
        console.error('Error fetching active tracking scripts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveScripts();
  }, []);

  return { scripts, loading };
}
